import { useEffect, useRef, useState } from 'react';

import FilterList from '@mui/icons-material/FilterList';
import {
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Link,
  redirect,
  useLocation,
  useNavigate,
  useNavigation,
  useSearchParams,
} from 'react-router';

import { get, getOr } from '@/api/client';
import { RUTAS } from '@/api/rutas';
import { comoRespuesta } from '@/lib/errores';
import { CampoDeBusqueda } from '@/components/CampoDeBusqueda';
import { MedirVisita } from '@/components/MedirVisita';
import { TablaDeProductos } from '@/components/TablaDeProductos';
import { TarjetaDeProducto } from '@/components/TarjetaDeProducto';
import { VistaRapida } from '@/components/VistaRapida';
import { MARCAS, type Marca, nombreDeMarca } from '@/lib/catalogo';
import { SITIO } from '@/lib/datos-estructurados';
import { slugDeRubro } from '@/lib/rubros';
import { medirBusqueda } from '@/lib/medir';
import { useDatosDelSitio } from '@/root';
import type { Imagen, Paginacion, Producto } from '@/tipos';

import type { Route } from './+types/catalogo';

/** Cuántos entran en cada vista. Son los mismos números de siempre. */
const POR_PAGINA = { grid: 12, table: 20 } as const;

const FILTROS_RAPIDOS: Record<string, string> = {
  incoming: 'Ingresos',
  offer: 'Ofertas',
  reposition: 'Reposición',
  bulk_discount: 'Descuento Bulto Cerrado',
};

interface Respuesta {
  products: Producto[];
  pagination: Paginacion;
}

/** Lo que pide la dirección, ya validado. */
export interface Filtros {
  busqueda: string;
  rubro: string;
  /** Una de `MARCAS`, o `'all'`. */
  marca: string;
  /** Una clave de `FILTROS_RAPIDOS`, o `'all'`. */
  rapido: string;
  /** Sólo lo que hay en stock, o todo. Va aparte de los atajos y se suma a cualquiera. */
  stock: 'available' | 'all';
  pagina: number;
  vista: 'grid' | 'table';
}

/**
 * Lee los filtros de la dirección. El loader y la pantalla usan esto mismo, así que no pueden
 * no estar de acuerdo.
 *
 * Lo que no es un valor conocido se toma como "no filtrar", que es lo que la API ya hacía por su
 * lado. La diferencia es que antes la pantalla no se enteraba: con `?quick=cualquiera` mostraba
 * el cartel "Filtro: cualquiera" sobre el catálogo entero, con todos los botones apagados.
 */
export function leerFiltros(parametros: URLSearchParams): Filtros {
  const marca = (parametros.get('brand') ?? '').toUpperCase();
  const rapido = parametros.get('quick') ?? '';
  const pagina = Number.parseInt(parametros.get('page') ?? '1', 10);

  return {
    busqueda: parametros.get('search') ?? '',
    rubro: parametros.get('category') ?? '',
    marca: MARCAS.includes(marca as Marca) ? marca : 'all',
    rapido: Object.keys(FILTROS_RAPIDOS).includes(rapido) ? rapido : 'all',
    stock: parametros.get('stock') === 'available' ? 'available' : 'all',
    pagina: Number.isNaN(pagina) || pagina < 1 ? 1 : pagina,
    vista: parametros.get('view') === 'table' ? 'table' : 'grid',
  };
}

export function meta({ data }: Route.MetaArgs): Route.MetaDescriptors {
  // Una dirección filtrada no es una página: es el catálogo con otra lista adentro. La del rubro
  // sí lo es, y por eso el filtro de rubro manda ahí; el resto de las combinaciones —búsqueda,
  // marca, atajos— no se indexan, pero se siguen: los productos que muestran sí valen.
  const direccion = data?.canonical
    ? [{ tagName: 'link' as const, rel: 'canonical', href: data.canonical }]
    : [{ name: 'robots', content: 'noindex, follow' }];

  return [
    ...direccion,
    { title: 'Catalogo de Productos | CKING Repuestos para Motos' },
    {
      name: 'description',
      content:
        'Explora el catalogo completo de repuestos para motocicletas de CKING. Filtra por marca ' +
        '(HADA, HAFEEN, VTC, ARTICULOS PARA BENELLI), categoria y disponibilidad.',
    },
    { property: 'og:title', content: 'Catalogo de Productos | CKING' },
    {
      property: 'og:description',
      content: 'Repuestos para motos: catalogo digital con miles de productos.',
    },
    { property: 'og:type', content: 'website' },
  ];
}

/**
 * El filtrado lo hace la base, no el navegador.
 *
 * Antes la pantalla se descargaba el catálogo entero —tres mil productos, un par de megas— y lo
 * filtraba en memoria. Eso tenía dos costos: la primera visita esperaba esa descarga mirando un
 * "Cargando catálogo…", y una dirección como /catalogo?search=frenos llegaba a Google como una
 * página vacía. Pidiendo sólo la página que se va a mostrar, el HTML sale con los productos ya
 * puestos.
 */
export async function loader({ request }: Route.LoaderArgs) {
  const direccion = new URL(request.url);
  const { vista, pagina, busqueda, rubro, marca, rapido, stock } = leerFiltros(
    direccion.searchParams,
  );

  // El catálogo es la pantalla, así que si no se puede pedir no hay nada que dibujar. Lo que sí
  // cambia es qué se dice: una API que no contesta es "probá en un momento" y no "algo se rompió
  // de nuestro lado, escribinos" —que para un corte de treinta segundos es mentira—.
  let catalogo: Respuesta;
  let rubros: { categories: string[] };

  try {
    [catalogo, rubros] = await Promise.all([
      get<Respuesta>(RUTAS.catalogo.productos, {
        request,
        query: {
          search: busqueda,
          category: rubro,
          // 'all' es lo que el desplegable llama "todas"; para la API es no filtrar.
          brand: marca === 'all' ? '' : marca,
          quick_filter: rapido === 'all' ? '' : rapido,
          stock: stock === 'all' ? '' : stock,
          page: pagina,
          limit: POR_PAGINA[vista],
        },
      }),
      getOr<{ categories: string[] }>(RUTAS.catalogo.categorias, { categories: [] }, { request }),
    ]);
  } catch (fallo) {
    throw comoRespuesta(fallo);
  }

  // Una página que ya no existe se lleva a la última que sí. Pasa con una dirección guardada
  // cuando el catálogo se achicó, o escrita a mano: sin esto la pantalla decía "No encontramos
  // esa pieza" con productos que coinciden con todos los filtros.
  const { total, totalPages } = catalogo.pagination;
  if (total > 0 && pagina > totalPages) {
    direccion.searchParams.set('page', String(totalPages));
    throw redirect(`${direccion.pathname}${direccion.search}`);
  }

  // Las fotos de los productos de esta página, en paralelo. Son doce o veinte consultas a una
  // API que está en la misma máquina; antes eran las mismas consultas pero desde el navegador,
  // de a cinco, con la grilla ya dibujada y llenándose de a poco.
  const fotos: Record<string, string | null> = Object.fromEntries(
    await Promise.all(
      catalogo.products.map(async producto => {
        const { images } = await getOr<{ images: Imagen[] }>(
          RUTAS.catalogo.fotosDelProducto(producto.code),
          { images: [] },
          { request },
        );

        return [producto.code, images[0]?.url ?? null] as const;
      }),
    ),
  );

  // Cuando la búsqueda no encontró nada, se pregunta de nuevo por el prefijo del código. Quien
  // escribe ARB999 casi siempre quiere algo de la familia ARB: ofrecerle los vecinos es más
  // útil que un cartel vacío. Sólo pasa en el caso vacío, así que no le cuesta nada al camino
  // normal.
  const sugerencias =
    catalogo.products.length === 0 && busqueda.trim().length >= 3
      ? (
          await getOr<Respuesta>(
            RUTAS.catalogo.productos,
            { products: [], pagination: { page: 1, limit: 0, total: 0, totalPages: 0 } },
            { request, query: { search: prefijoDeCodigo(busqueda), page: 1, limit: 6 } },
          )
        ).products
      : [];

  // Las fotos de las sugeridas se piden acá y no en la grilla normal: son seis consultas que
  // sólo ocurren cuando la búsqueda no encontró nada. Una tarjeta sugerida sin foto es una
  // tarjeta que no ayuda a reconocer la pieza, que es justamente para lo que están.
  for (const [codigo, url] of await Promise.all(
    sugerencias.map(async sugerido => {
      const { images } = await getOr<{ images: Imagen[] }>(
        RUTAS.catalogo.fotosDelProducto(sugerido.code),
        { images: [] },
        { request },
      );

      return [sugerido.code, images[0]?.url ?? null] as const;
    }),
  )) {
    fotos[codigo] = url;
  }

  return {
    ...catalogo,
    rubros: rubros.categories,
    fotos,
    vista,
    sugerencias,
    canonical: canonica({ busqueda, rubro, marca, rapido, stock, pagina }),
  };
}

/**
 * El principio del código, para buscar los parientes de algo que no existe.
 *
 * `ARB999` -> `ARB`. Se corta en el primer dígito porque los códigos de CKING son letras de
 * familia y después número: `ACS001-4`, `VNA302-1`. Si no hay letras al principio -alguien
 * buscó por descripción- se devuelven las primeras tres, que es lo mismo que hacía la búsqueda.
 */
export function prefijoDeCodigo(busqueda: string): string {
  const limpio = busqueda.trim().toUpperCase();
  const letras = /^[A-Z]+/.exec(limpio)?.[0];

  return letras && letras.length >= 2 ? letras : limpio.slice(0, 3);
}

export default function Catalogo({ loaderData }: Route.ComponentProps) {
  const { products, pagination, rubros, fotos, vista, sugerencias } = loaderData;
  const { sesion } = useDatosDelSitio();

  const [parametros, setParametros] = useSearchParams();
  const navigate = useNavigate();

  const { busqueda: busquedaEnUrl, rubro, marca, rapido, stock } = leerFiltros(parametros);

  /** El producto que se está mirando de refilón, o null. */
  const [mirando, setMirando] = useState<Producto | null>(null);

  /**
   * Cuántas veces se apretó "Limpiar filtros".
   *
   * Es la identidad del buscador: al cambiar, el campo nace de nuevo y lo escrito a medias se va
   * con él. Hace falta porque limpiar con el campo a medio escribir no cambia la dirección —la
   * búsqueda todavía no había llegado— y sin esto el texto se quedaba ahí y volvía a buscarse
   * solo 300ms después.
   */
  const [limpiezas, setLimpiezas] = useState(0);

  // Si lo que se fue a buscar todavía no volvió. Es sólo para avisarlo en el campo: la lista que
  // se ve abajo sigue siendo la anterior hasta que llegue la nueva.
  const navegacion = useNavigation();
  const ubicacion = useLocation();
  const buscando =
    navegacion.state === 'loading' &&
    navegacion.location.pathname === ubicacion.pathname &&
    (new URLSearchParams(navegacion.location.search).get('search') ?? '') !== busquedaEnUrl;

  /**
   * Lo escrito, ya quieto, pasa a la dirección.
   *
   * Va aparte de `cambiar` porque acá el texto es texto: buscar "all" es buscar "all", y no
   * "ningún filtro". Sólo vaciarlo saca la búsqueda de la dirección.
   */
  function buscar(texto: string) {
    const proximos = new URLSearchParams(parametros);

    if (texto) proximos.set('search', texto);
    else proximos.delete('search');

    proximos.set('page', '1');

    // replace: escribir "freno" no deja cinco entradas en el historial para volver atrás.
    setParametros(proximos, { replace: true, preventScrollReset: true });
  }

  /** Cambia un filtro y vuelve a la primera página: la 12 de la búsqueda anterior no existe. */
  function cambiar(clave: string, valor: string) {
    const proximos = new URLSearchParams(parametros);

    if (valor && valor !== 'all') proximos.set(clave, valor);
    else proximos.delete(clave);

    proximos.set('page', '1');
    setParametros(proximos, { replace: true, preventScrollReset: true });
  }

  function limpiar() {
    setLimpiezas(veces => veces + 1);
    void navigate('/catalogo');
  }

  const conPrecios = sesion !== null;

  // Quien pidió que las cosas no se muevan ve la grilla puesta, sin la entrada escalonada.
  const sinMovimiento = useReducedMotion();

  const fotosDeTabla: Record<string, string> = {};
  for (const [codigo, url] of Object.entries(fotos)) {
    if (url) fotosDeTabla[codigo] = url;
  }

  return (
    <Box sx={{ py: 4, bgcolor: '#121212', minHeight: '100vh' }}>
      <MedirVisita path="/catalogo" />

      <MedirLaBusqueda
        texto={busquedaEnUrl}
        resultados={pagination.total}
        rubro={rubro}
        marca={marca}
        rapido={rapido}
      />

      <Container maxWidth="xl">
        <Typography
          variant="h3"
          component="h1"
          sx={{
            mb: 4,
            fontWeight: 700,
            textAlign: 'center',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              width: '60px',
              height: '3px',
              bottom: '-10px',
              left: '50%',
              backgroundColor: 'primary.main',
              transform: 'translateX(-50%)',
            },
          }}
        >
          Catálogo de Productos
        </Typography>

        <Box
          sx={{
            mb: 4,
            p: { xs: 1.5, sm: 3 },
            bgcolor: '#1a1a1a',
            borderRadius: 1,
            border: '1px solid rgba(255, 255, 255, 0.05)',
          }}
        >
          <Grid container spacing={3}>
            <Grid>
              <CampoDeBusqueda
                key={limpiezas}
                valor={busquedaEnUrl}
                buscando={buscando}
                onBuscar={buscar}
              />
            </Grid>

            <Grid>
              <FormControl fullWidth sx={{ minWidth: { xs: '100%', sm: 300 } }}>
                <InputLabel id="rubro">Categoría</InputLabel>
                <Select
                  labelId="rubro"
                  label="Categoría"
                  value={rubro}
                  onChange={evento => cambiar('category', evento.target.value)}
                >
                  <MenuItem value="">Todas las categorías</MenuItem>
                  {rubros.map(nombre => (
                    <MenuItem key={nombre} value={nombre}>
                      {nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid>
              <FormControl fullWidth sx={{ minWidth: { xs: '100%', sm: 300 } }}>
                <InputLabel id="marca">Marca</InputLabel>
                <Select
                  labelId="marca"
                  label="Marca"
                  value={marca}
                  onChange={evento => cambiar('brand', evento.target.value)}
                >
                  <MenuItem value="all">Todas las marcas</MenuItem>
                  {MARCAS.map(nombre => (
                    <MenuItem key={nombre} value={nombre}>
                      {nombreDeMarca(nombre)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={limpiar}
                startIcon={<FilterList />}
                sx={{
                  color: '#ff6659',
                  borderColor: '#ff6659',
                  '&:hover': {
                    borderColor: '#ff8a80',
                    backgroundColor: 'rgba(255, 102, 89, 0.08)',
                  },
                }}
              >
                Limpiar filtros
              </Button>
            </Grid>
          </Grid>

          {/* Los atajos salen de la columna ESTADO de la planilla. */}
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant={rapido === 'all' ? 'contained' : 'outlined'}
              color="primary"
              size="small"
              onClick={() => cambiar('quick', 'all')}
            >
              Todos
            </Button>

            <Button
              variant={rapido === 'incoming' ? 'contained' : 'outlined'}
              color="info"
              size="small"
              onClick={() => cambiar('quick', 'incoming')}
            >
              Ingresos
            </Button>

            <Button
              variant={rapido === 'offer' ? 'contained' : 'outlined'}
              color="warning"
              size="small"
              onClick={() => cambiar('quick', 'offer')}
            >
              Ofertas
            </Button>

            <Button
              variant={rapido === 'reposition' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => cambiar('quick', 'reposition')}
              sx={{
                color: rapido === 'reposition' ? '#fff' : '#bb86fc',
                borderColor: '#bb86fc',
                backgroundColor: rapido === 'reposition' ? '#7c4dff' : 'transparent',
                '&:hover': {
                  borderColor: '#d1a8ff',
                  backgroundColor:
                    rapido === 'reposition' ? '#6a3cd9' : 'rgba(187, 134, 252, 0.08)',
                },
              }}
            >
              Reposición
            </Button>

            <Button
              variant={rapido === 'bulk_discount' ? 'contained' : 'outlined'}
              color="success"
              size="small"
              onClick={() => cambiar('quick', 'bulk_discount')}
            >
              Descuento Bulto Cerrado
            </Button>

            {/* Aparte de los atajos, que se excluyen entre sí: éste se suma a cualquiera. "Ofertas
                con stock" es una pregunta que tiene sentido; "ofertas o con stock", no. */}
            <Button
              variant={stock === 'available' ? 'contained' : 'outlined'}
              color="success"
              size="small"
              aria-pressed={stock === 'available'}
              onClick={() => cambiar('stock', stock === 'available' ? 'all' : 'available')}
              sx={{ ml: { sm: 'auto' } }}
            >
              Con stock
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            mb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            // Se queda pegada abajo de la cabecera. Con 2831 productos paginados de a doce, los
            // filtros y el conmutador de vista se iban para arriba y había que volver a buscarlos
            // para cambiar una cosa. Los 70 son el alto de la barra fija del sitio.
            position: 'sticky',
            top: 70,
            zIndex: 3,
            py: 1.5,
            // El fondo tiene que ser opaco o las tarjetas se ven pasar por debajo del texto. El
            // desenfoque es lo que hace que se lea como una capa y no como un parche.
            bgcolor: 'rgba(18, 18, 18, .92)',
            backdropFilter: 'blur(8px)',
            borderBottom: '1px solid rgba(255, 255, 255, .06)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1">{pagination.total} productos encontrados</Typography>
            {conPrecios && <Chip label="Precios visibles" color="success" size="small" />}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {busquedaEnUrl && (
              <Chip
                label={`Búsqueda: ${busquedaEnUrl}`}
                onDelete={() => cambiar('search', '')}
                color="info"
                variant="outlined"
              />
            )}

            {rubro && (
              <Chip
                label={`Categoría: ${rubro}`}
                onDelete={() => cambiar('category', '')}
                color="info"
                variant="outlined"
              />
            )}

            {marca !== 'all' && (
              <Chip
                label={`Marca: ${nombreDeMarca(marca)}`}
                onDelete={() => cambiar('brand', 'all')}
                color="info"
                variant="outlined"
              />
            )}

            {rapido !== 'all' && (
              <Chip
                label={`Filtro: ${FILTROS_RAPIDOS[rapido] ?? rapido}`}
                onDelete={() => cambiar('quick', 'all')}
                color="info"
                variant="outlined"
              />
            )}

            {stock === 'available' && (
              <Chip
                label="Con stock"
                onDelete={() => cambiar('stock', 'all')}
                color="info"
                variant="outlined"
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={vista === 'grid' ? 'contained' : 'outlined'}
              size="small"
              color="primary"
              onClick={() => cambiar('view', 'grid')}
            >
              Cuadrícula
            </Button>

            <Button
              variant={vista === 'table' ? 'contained' : 'outlined'}
              size="small"
              color="primary"
              onClick={() => cambiar('view', 'table')}
            >
              Tabla
            </Button>
          </Box>
        </Box>

        {products.length === 0 ? (
          <Box role="status" aria-live="polite" sx={{ py: { xs: 6, md: 10 } }}>
            <Box sx={{ maxWidth: 720, mx: 'auto', textAlign: 'center' }}>
              <Typography variant="h2" component="p" sx={{ mb: 1 }}>
                No encontramos esa pieza
              </Typography>

              <Typography sx={{ color: 'text.secondary', mb: 3 }}>
                {busquedaEnUrl
                  ? `Ningún producto responde a «${busquedaEnUrl}» con los filtros puestos.`
                  : 'Ningún producto coincide con los filtros puestos.'}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button variant="contained" color="primary" onClick={limpiar}>
                  Limpiar filtros
                </Button>

                <Button component={Link} to="/rubros" variant="outlined" color="primary">
                  Ver por rubro
                </Button>
              </Box>
            </Box>

            {sugerencias.length > 0 && (
              <Box sx={{ mt: 8 }}>
                <Typography variant="h3" component="p" sx={{ textAlign: 'center', mb: 1 }}>
                  Piezas de la misma familia
                </Typography>

                <Typography sx={{ color: 'text.secondary', textAlign: 'center', mb: 4 }}>
                  Los códigos que empiezan igual que lo que buscaste.
                </Typography>

                <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ justifyContent: 'center' }}>
                  {sugerencias.map(sugerido => (
                    <Grid key={sugerido.code} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                      <TarjetaDeProducto
                        producto={sugerido}
                        foto={fotos[sugerido.code] ?? null}
                        conPrecios={conPrecios}
                        onVistaRapida={setMirando}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Box>
        ) : (
          <>
            {vista === 'grid' ? (
              <Grid
                container
                spacing={{ xs: 2, sm: 3 }}
                sx={{ maxWidth: '100%', justifyContent: 'center' }}
              >
                {products.map((producto, indice) => (
                  <Grid key={producto.code} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <motion.div
                      initial={sinMovimiento ? false : { opacity: 0, y: 14 }}
                      animate={{ opacity: 1, y: 0 }}
                      // 30ms entre tarjeta y tarjeta: alcanza para que la grilla se lea como
                      // algo que se arma y no tanto como para hacer esperar. Se corta en la
                      // octava porque a partir de ahí el retraso se nota más que el efecto.
                      transition={{
                        duration: 0.35,
                        delay: Math.min(indice, 8) * 0.03,
                        ease: [0.22, 0.61, 0.36, 1],
                      }}
                      style={{ height: '100%' }}
                    >
                      <TarjetaDeProducto
                        producto={producto}
                        foto={fotos[producto.code] ?? null}
                        conPrecios={conPrecios}
                        onVistaRapida={setMirando}
                      />
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <TablaDeProductos productos={products} fotos={fotosDeTabla} conPrecios={conPrecios} />
            )}

            {pagination.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.page}
                  onChange={(_, siguiente) => {
                    const proximos = new URLSearchParams(parametros);
                    proximos.set('page', String(siguiente));
                    setParametros(proximos, { preventScrollReset: true });
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  color="primary"
                  size="large"
                  aria-label="Paginacion de productos"
                  getItemAriaLabel={(tipo, numero, seleccionada) => {
                    if (tipo === 'page') return `${seleccionada ? '' : 'Ir a '}pagina ${numero}`;
                    if (tipo === 'next') return 'Ir a la pagina siguiente';
                    if (tipo === 'previous') return 'Ir a la pagina anterior';
                    return '';
                  }}
                  sx={{
                    '& .MuiPaginationItem-root': { color: 'white', minWidth: 44, minHeight: 44 },
                  }}
                />
              </Box>
            )}
          </>
        )}

        <VistaRapida
          producto={mirando}
          foto={mirando ? (fotos[mirando.code] ?? null) : null}
          conPrecios={conPrecios}
          onCerrar={() => setMirando(null)}
        />
      </Container>
    </Box>
  );
}

/**
 * La dirección buena de esta vista del catálogo, o null si no hay ninguna que valga indexar.
 *
 * Sin filtros es el catálogo mismo, con su número de página. Con el rubro solo, la página del
 * rubro, que es la que tiene título y texto propios. Con cualquier otra combinación no hay página
 * que ofrecer: son listas armadas para quien ya está adentro mirando.
 */
export function canonica(filtros: Omit<Filtros, 'vista'>): string | null {
  const otrosFiltros =
    filtros.busqueda.trim() !== '' ||
    filtros.marca !== 'all' ||
    filtros.rapido !== 'all' ||
    filtros.stock !== 'all';

  if (otrosFiltros) return null;

  const pagina = filtros.pagina > 1 ? `?page=${filtros.pagina}` : '';

  return filtros.rubro === ''
    ? `${SITIO}/catalogo${pagina}`
    : `${SITIO}/rubros/${slugDeRubro(filtros.rubro)}${pagina}`;
}

/**
 * Cuenta la búsqueda una vez, cuando quedó quieta.
 *
 * Lo que interesa medir es lo que la persona terminó buscando —"cadena 428"— y no cada estado
 * intermedio: "c", "ca", "cad". Por eso se cuenta el término que ya llegó a la dirección, que es
 * el que sobrevivió a los 300ms de silencio, y sólo cuando cambió de verdad.
 */
function MedirLaBusqueda({
  texto,
  resultados,
  rubro,
  marca,
  rapido,
}: {
  texto: string;
  resultados: number;
  rubro: string;
  marca: string;
  rapido: string;
}) {
  const ultima = useRef<string | null>(null);

  useEffect(() => {
    const limpio = texto.trim();

    // Menos de tres letras no es una búsqueda: es alguien que recién empieza a escribir.
    if (limpio.length < 3 || limpio === ultima.current) return;

    ultima.current = limpio;

    medirBusqueda({
      query: limpio,
      result_count: resultados,
      brand_filter: marca === 'all' ? null : marca,
      category_filter: rubro || null,
      quick_filter: rapido === 'all' ? null : rapido,
    });
  }, [texto, resultados, rubro, marca, rapido]);

  return null;
}
